import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import * as config from "config";
import DirectChat from "./DirectChat";
import css from "./directChatModal.module.scss";

/* ─────────────────────────────────────────────────────────────
   Context
   ───────────────────────────────────────────────────────────── */
const DirectChatModalContext = createContext(null);

/** Hook for any child to open / close the chat modal */
export function useDirectChatModal() {
  const ctx = useContext(DirectChatModalContext);
  if (!ctx) {
    throw new Error("useDirectChatModal must be used within DirectChatModalProvider");
  }
  return useMemo(
    () => ({ openDirectChat: ctx.openDirectChat, closeDirectChat: ctx.closeDirectChat }),
    [ctx.openDirectChat, ctx.closeDirectChat]
  );
}

/* ─────────────────────────────────────────────────────────────
   Inline SVG icon for the header (no MUI dependency)
   ───────────────────────────────────────────────────────────── */
function ChatHeaderIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   Modal gate — rendered via React portal at document.body
   ───────────────────────────────────────────────────────────── */
function DirectChatModalGate() {
  const ctx = useContext(DirectChatModalContext);
  const history = useHistory();
  const location = useLocation();

  // Animate-out state: when we want to close, we first play the exit animation,
  // then actually hide after the animation duration.
  const [visible, setVisible] = useState(false);   // is DOM present
  const [exiting, setExiting] = useState(false);   // is exit animation playing

  const skipCloseOnNextNav = useRef(false);
  const modalOpenRef = useRef(false);
  const exitTimer = useRef(null);

  const open = ctx?.open ?? false;
  const seedPeerMemberId = ctx?.seedPeerMemberId ?? null;
  const seedStudentMemberId = ctx?.seedStudentMemberId ?? null;
  const openDirectChat = ctx?.openDirectChat;
  const closeDirectChat = ctx?.closeDirectChat;
  const panelKey = ctx?.panelKey ?? 0;

  /* Sync open → visible with entry/exit animation */
  useEffect(() => {
    if (open) {
      if (exitTimer.current) {
        clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setExiting(false);
      setVisible(true);
      modalOpenRef.current = true;
    } else {
      if (visible) {
        setExiting(true);
        exitTimer.current = setTimeout(() => {
          setVisible(false);
          setExiting(false);
        }, 200); // matches CSS animation duration
      }
      modalOpenRef.current = false;
    }
    return () => { if (exitTimer.current) clearTimeout(exitTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Close on navigation (same logic as before) */
  useEffect(() => {
    const unlisten = history.listen(() => {
      if (skipCloseOnNextNav.current) {
        skipCloseOnNextNav.current = false;
        return;
      }
      if (modalOpenRef.current && closeDirectChat) {
        closeDirectChat();
      }
    });
    return unlisten;
  }, [history, closeDirectChat]);

  /* Handle direct /directChat?peer= route */
  useEffect(() => {
    if (!openDirectChat) return;
    if (location.pathname !== config.ROUTES.directChat) return;
    const q = new URLSearchParams(location.search);
    const raw = q.get("peer");
    const peerNum = raw != null ? Number(raw) : null;
    const peerMemberId = Number.isFinite(peerNum) ? peerNum : null;
    openDirectChat({ peerMemberId });
    skipCloseOnNextNav.current = true;
    history.replace(config.ROUTES.home);
  }, [location.pathname, location.search, history, openDirectChat]);

  /* Close on Escape key */
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape" && closeDirectChat) closeDirectChat();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, closeDirectChat]);

  /* Prevent body scroll while open */
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  if (!ctx || !visible) return null;

  const overlayClass = exiting ? `${css.overlay} ${css.overlayExit}` : css.overlay;
  const panelClass   = exiting ? `${css.panel}   ${css.panelExit}`   : css.panel;

  return ReactDOM.createPortal(
    <div
      className={overlayClass}
      role="dialog"
      aria-modal="true"
      aria-label="Direct Messages"
      onMouseDown={(e) => {
        // Close only if clicking the backdrop directly
        if (e.target === e.currentTarget && closeDirectChat) closeDirectChat();
      }}
    >
      <div className={panelClass}>
        {/* Header */}
        <div className={css.modalHeader}>
          <h2 className={css.modalTitle}>
            <span className={css.modalTitleIcon}>
              <ChatHeaderIcon />
            </span>
            Messages
          </h2>
          <button
            type="button"
            className={css.closeBtn}
            onClick={closeDirectChat}
            aria-label="Close chat"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className={css.body}>
          <DirectChat
            key={panelKey}
            variant="modal"
            initialPeerMemberId={seedPeerMemberId}
            studentMemberId={seedStudentMemberId}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────────────────────
   Provider
   ───────────────────────────────────────────────────────────── */
export function DirectChatModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [seedPeerMemberId, setSeedPeerMemberId] = useState(null);
  const [seedStudentMemberId, setSeedStudentMemberId] = useState(null);
  const [panelKey, setPanelKey] = useState(0);

  const openDirectChat = useCallback((opts) => {
    setSeedPeerMemberId(
      opts && opts.peerMemberId != null && Number.isFinite(Number(opts.peerMemberId))
        ? Number(opts.peerMemberId)
        : null
    );
    setSeedStudentMemberId(
      opts && opts.studentMemberId != null && Number.isFinite(Number(opts.studentMemberId))
        ? Number(opts.studentMemberId)
        : null
    );
    setPanelKey((k) => k + 1);
    setOpen(true);
  }, []);

  const closeDirectChat = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      open,
      seedPeerMemberId,
      seedStudentMemberId,
      openDirectChat,
      closeDirectChat,
      panelKey,
    }),
    [open, seedPeerMemberId, seedStudentMemberId, openDirectChat, closeDirectChat, panelKey]
  );

  return (
    <DirectChatModalContext.Provider value={value}>
      {children}
      <DirectChatModalGate />
    </DirectChatModalContext.Provider>
  );
}
